<template>
  <div>
    <b-button
      ref="reviewForPublicationButton"
      :disabled="isSubmittedReview"
      variant="success"
      @click="onClickReviewPublication"
      >Review for publication</b-button
    >

    <b-modal
      id="ModalReviewPublication"
      ref="ModalReviewPublication"
      v-model="showModal"
      :size="'lg'"
      centered
      static
    >
      <template #modal-header="{}">
        <!-- Emulate built in modal header close button action -->
        <div>
          <div class="small text-dark mb-1">AGREEMENT</div>
          <h4
            id="ModalReviewPublicationConsentTypeName"
            ref="consentTypeName"
            class="font-weight-bold"
          >
            {{ consentType.name }}
          </h4>
          <div
            id="ModalReviewPublicationConsentTypeDescription"
            ref="consentTypeDescription"
            class="small text-dark mb-1"
          >
            {{ consentType.description }}
          </div>
        </div>
      </template>

      <template #default="{}">
        <b-form-group v-for="agreement in agreements" :key="agreement.id" class="mt-3">
          <b-form-checkbox
            :ref="`agreement-checkbox-${agreement.id}`"
            v-model="selected"
            :class="`${agreement.length - 1 ? '' : 'my-1'}`"
            :value="agreement.id"
            name="agreement-options"
          >
            <div>
              <div class="font-weight-bold">{{ agreement.title }}</div>
              <div>{{ agreement.description }}</div>
            </div>
          </b-form-checkbox>
        </b-form-group>
      </template>

      <template #modal-footer="{}" class="d-flex justify-content-start">
        <div class="footer pt-4">
          <b-button
            id="button-submit"
            ref="button-submit"
            variant="success"
            :disabled="isDisabledSubmit || submitting"
            @click="submit"
          >
            <span v-if="submitting" class="pr-2 pl-2">
              <b-spinner label="Loading..." variant="secondary" small />
            </span>
            <span v-else>Submit</span>
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { publicationMixin } from '@/mixins/publicationMixin';
import LearningUnit from '@/models/LearningUnit';
import ConsentType from '@/models/ConsentType';

export default {
  mixins: [publicationMixin],

  props: {
    learningUnit: {
      type: LearningUnit,
      required: true
    },
    todos: {
      type: Array,
      default: () => []
    },
    conflicts: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isLoading: false,
      showModal: false,
      submitting: false,
      selected: [],
      agreements: [
        {
          id: 1,
          title: 'Reviewer Authorities',
          description:
            'I agree that reviewer has authority to change information in this micro-credential without prior notice.'
        },
        {
          id: 2,
          title: 'Learning resources license',
          description:
            'I confirm that this submission does not include any personally identifiable data and does not violate any laws.'
        },
        {
          id: 3,
          title: 'Valid learning resources',
          description:
            'I confirm that this submission does not contain plagiarism and is my own original work. I have included appropriate citations and credits in compliance with any applicable academic integrity policy. I understand that if plagiarism or any violations are found, KMUTT may report this submission or take any other action permitted by KMUTT’s regulations.'
        },
        {
          id: 4,
          title: 'Submit for publication',
          description:
            'After you confirmed you can’t edit any information of this micro-credential anymore. For 10 days due to our reviewing your micro-credential.'
        },
        {
          id: 5,
          title: 'Session time',
          description: 'You cannot change your session time anymore after the session was published'
        }
      ]
    };
  },

  computed: {
    consentType() {
      let name = '',
        description = '';

      const consentType = ConsentType.query()
        .where('name', 'Submit for Review and Publication')
        .first();

      if (consentType) {
        name = consentType.name;
        description = consentType.description;
      }

      return {
        name,
        description
      };
    },

    canSubmitForReview() {
      let canSubmitForReview = false;

      if (this.todos && this.conflicts) {
        const countPendingTodo = this.$_publicationMixin_countPendingTodo(this.todos);
        const hasSomeConflicts = this.$_publicationMixin_haveSomeConflict(this.conflicts);

        canSubmitForReview = countPendingTodo === 0 && !hasSomeConflicts;
      }

      return canSubmitForReview;
    },

    isSubmittedReview() {
      return this.learningUnit.required_review;
    },

    isDisabledSubmit() {
      return !(this.selected.length !== 0 && this.agreements.length === this.selected.length);
    }
  },

  async mounted() {
    await this.fetchConsentType();
  },

  methods: {
    onClickReviewPublication() {
      // if (this.canSubmitForReview) {
      //   this.showModal = true;
      // } else {
      //   this.goToTabPublication();
      //   this.$toast.error("You still have needed things to do for publication");
      // }
      this.showModal = true;
    },

    async submit() {
      this.submitting = true;

      try {
        await LearningUnit.api().submitForPublication(this.learningUnit.id);

        this.showModal = false;

        this.$toast.success('This micro-credential has been successfully submitted for review');
      } catch (error) {
        this.$toast.error('Cannot submit this micro-credential to review, please try again');
      } finally {
        setTimeout(() => {
          this.submitting = false;
        }, 1000);
      }
    },

    async fetchConsentType() {
      try {
        await ConsentType.api().fetch({
          name: 'Submit for Review and Publication'
        });
      } catch (error) {
        console.error(error);
        this.$toast.error('Cannot load publication agreement, please try again');
      } finally {
        this.isLoading = false;
      }
    },

    goToTabPublication() {
      if (this.$route.query.tab != 'to-do-for-publication') {
        this.$router.replace({
          query: {
            tab: 'to-do-for-publication'
          }
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1rem 0rem 1rem;
  border-bottom: none !important;
}
::v-deep .modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: 0rem 1rem 0rem 1rem;
}
::v-deep .modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  padding: 0rem 1rem 1rem 1rem;
  border: none !important;
}
::v-deep .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.4) !important;
}
</style>
